import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { Paginator } from 'primereact/paginator';

import LayoutPreview from '../../LayoutPreview';

import { setDisableBtn, toggleDialog } from '../../../../../../redux/Slices/Global/globalSlice';
import { updateOriginalScreens } from '../../../../../../config/layoutHelper';
import { setSelectedTemplate } from '../../../../../../redux/Slices/layoutSlice';
import { useUpdateLayout } from '../../../../../../queries/layouts';
import LayoutConflictWarning from './LayoutConflictWarning';

const ChangeCustomLayouts = ({ currentPage, setCurrentPage, totalRecords }) => {
    const { t } = useTranslation();
    const { id } = useParams();

    const dispatch = useDispatch()
    const rowsPerPage = 19;
    const { userTemplate, layoutAspectRatio, selectedLayout, screens_setting, selectedTemplate } = useSelector((state) => state.layout);
    const { subLoaders, openDialog } = useSelector((state) => state.global);
    const [isConflictScreens, setIsConflictScreens] = useState(false);
    const [newLayout, setNewLayout] = useState([]);
    const [oldLayout, setOldLayout] = useState(selectedLayout.screens_setting);
    const [areas, setAreas] = useState({});
    const [oldAspectRatio, setOldAspectRatio] = useState({});
    const [hoveredCard, setHoveredCard] = useState(null);

    const isTemplateSelectTriggered = useRef(false);
    const { mutate: updateLayout, isLoading } = useUpdateLayout();

    useEffect(() => {
        setOldAspectRatio(layoutAspectRatio)
    }, []);

    useEffect(() => {
        if (isTemplateSelectTriggered.current) {
            handleTemplateSelect();
            isTemplateSelectTriggered.current = false;
        }
    }, [selectedTemplate]);

    const handleTemplateSelect = () => {
        const sanitizedAreas = selectedTemplate.screens_setting;
        let originalScreens = screens_setting
        if (sanitizedAreas.length < originalScreens.length) {
            setOldLayout(selectedLayout.screens_setting)
            setNewLayout(sanitizedAreas)
            dispatch(toggleDialog("layoutConflict"))
        } else {
            handleChange(screens_setting, selectedTemplate)
        }
    }

    const handleChange = (screens_setting, selectedTemplate) => {
        dispatch(setDisableBtn(true));
        const sanitizedAreas = selectedTemplate.screens_setting;
        let originalScreens = screens_setting
        const theNewAspect = selectedTemplate.aspect_ratio.id
        const theUpdatedVersion = updateOriginalScreens(originalScreens, sanitizedAreas);
        updateLayout(
            {
                layoutId: id,
                data: {
                    title: selectedLayout.title,
                    screens_setting: sanitizedAreas,
                    type: "CUSTOM",
                    status: "ACTIVE",
                    aspect_ratio_id: theNewAspect,
                    updated_campaigns: theUpdatedVersion
                },
            },
            {
                onSuccess: () => {
                    dispatch(toggleDialog("EditLayoutPopUp"));
                },
            }
        );
    };

    const onPageChange = (event) => {
        const newPage = event.page;
        setCurrentPage(newPage);
    };

    return (
        <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30">
            {
                subLoaders.createLoading ?
                    <div className="flex-1 flex justify-center items-center">
                        <div className="relative">
                            {/* Animated Rings */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-24 h-24 border-4 border-indigo-200 rounded-full animate-ping opacity-20"></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-20 h-20 border-4 border-purple-200 rounded-full animate-ping opacity-30 animation-delay-150"></div>
                            </div>
                            
                            {/* Main Spinner */}
                            <div className="relative w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <i className="bx bx-layout text-indigo-600 text-2xl animate-pulse"></i>
                            </div>
                        </div>
                    </div>
                    :
                    <>
                        {/* Enhanced Header with Stats */}
                        <div className="relative px-8 py-6 bg-white border-b border-gray-200 shadow-sm">
                            {/* Decorative Background */}
                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-500/10 to-transparent rounded-full blur-3xl -mr-32 -mt-32"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-3xl -ml-32 -mb-32"></div>
                            
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    {/* Icon */}
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl blur-lg opacity-50"></div>
                                        <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-xl">
                                            <i className="bx bx-grid-alt text-white text-2xl"></i>
                                        </div>
                                    </div>
                                    
                                    {/* Title */}
                                    <div>
                                        <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                            {t('layouts.setup_templates_gallery')}
                                        </h2>
                                        <p className="text-sm text-gray-600 mt-0.5">{t('layouts.setup_templates_gallery_subtitle')}</p>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="flex items-center gap-4">
                                    <div className="px-4 py-2 rounded-xl bg-white border border-indigo-200 shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                                                <i className="bx bx-collection text-white text-sm"></i>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 font-medium">{t('layouts.templates_layouts')}</div>
                                                <div className="text-lg font-bold text-gray-800">{userTemplate.length}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Templates Grid - Full Width */}
                        <div className="flex-1 overflow-y-auto px-8 py-6">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
                                {userTemplate.map((template, index) => {
                                    const random5Digits = Math.floor(Math.random() * 90000) + 10000;
                                    const isHovered = hoveredCard === index;
                                    
                                    return (
                                        <div 
                                            key={`${index}-${random5Digits}`}
                                            className="group relative"
                                            onMouseEnter={() => setHoveredCard(index)}
                                            onMouseLeave={() => setHoveredCard(null)}
                                        >
                                            {/* Glow Effect */}
                                            <div className={`absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl blur-xl transition-all duration-500 ${
                                                isHovered ? 'opacity-30 scale-105' : 'opacity-0 scale-95'
                                            }`}></div>

                                            {/* Card */}
                                            <div className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-500 border-2 ${
                                                isHovered 
                                                    ? 'border-indigo-400 shadow-2xl shadow-indigo-500/30 -translate-y-3 scale-105' 
                                                    : 'border-gray-200 shadow-lg hover:shadow-xl'
                                            }`}>
                                                {/* Animated Gradient Overlay */}
                                                <div className={`absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 transition-opacity duration-500 ${
                                                    isHovered ? 'opacity-100' : 'opacity-0'
                                                }`}></div>

                                                {/* Decorative Corner */}
                                                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-500/20 to-transparent rounded-bl-full transition-opacity duration-500 ${
                                                    isHovered ? 'opacity-100' : 'opacity-0'
                                                }`}></div>

                                                {/* Header */}
                                                <div className="relative p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 border-b border-gray-200">
                                                    <div className="flex items-start justify-between gap-2 mb-3">
                                                        <h3 className="text-sm font-bold text-gray-800 truncate flex-1 leading-tight">{template.title}</h3>
                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ${
                                                            isHovered 
                                                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg scale-110' 
                                                                : 'bg-white shadow-sm'
                                                        }`}>
                                                            <i className={`bx bx-layout text-lg transition-colors duration-300 ${
                                                                isHovered ? 'text-white' : 'text-indigo-600'
                                                            }`}></i>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Info Badges */}
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-xs font-semibold text-indigo-700 shadow-sm">
                                                            <i className="bx bx-crop text-indigo-500"></i>
                                                            <span>{template.aspect_ratio?.width}:{template.aspect_ratio?.height}</span>
                                                        </div>
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-purple-200 text-xs font-semibold text-purple-700 shadow-sm">
                                                            <i className="bx bx-grid-small text-purple-500"></i>
                                                            <span>{template.screens_setting?.length || 0}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Preview */}
                                                <div className="relative p-4 bg-white">
                                                    <div className={`relative rounded-xl overflow-hidden transition-all duration-500 ${
                                                        isHovered ? 'ring-4 ring-indigo-400 ring-offset-2 shadow-xl' : 'ring-1 ring-gray-200'
                                                    }`}>
                                                        <LayoutPreview 
                                                            template={template} 
                                                            screens={template.screens_setting} 
                                                            onClick={() => {
                                                                dispatch(setSelectedTemplate(template));
                                                                isTemplateSelectTriggered.current = true;
                                                            }} 
                                                        />
                                                        

                                                    </div>
                                                </div>

                                                {/* Select Button */}
                                                <div className="p-4 pt-0">
                                                    <button
                                                        className={`w-full py-3 rounded-xl font-bold transition-all duration-500 flex items-center justify-center gap-2 relative overflow-hidden group/btn ${
                                                            isHovered
                                                                ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-xl shadow-indigo-500/40 scale-105'
                                                                : 'bg-gradient-to-r from-slate-100 to-slate-200 text-gray-700 hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-700 shadow-md'
                                                        }`}
                                                        onClick={() => {
                                                            dispatch(setSelectedTemplate(template));
                                                            isTemplateSelectTriggered.current = true;
                                                        }}
                                                    >
                                                        {/* Animated Background */}
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-1000"></div>
                                                        
                                                        <i className={`bx ${isHovered ? 'bx-check-circle' : 'bx-select-multiple'} text-xl relative z-10 transition-transform duration-300 ${
                                                            isHovered ? 'scale-110' : ''
                                                        }`}></i>
                                                        <span className="relative z-10">{isHovered ? t('layouts.setup_apply_template') : t('buttons.select')}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Empty State */}
                            {userTemplate.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <div className="relative mb-6">
                                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-2xl opacity-20 animate-pulse"></div>
                                        <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                                            <i className="bx bx-layout text-6xl bg-gradient-to-br from-indigo-500 to-purple-600 bg-clip-text text-transparent"></i>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{t('layouts.setup_no_templates')}</h3>
                                    <p className="text-gray-600 text-center max-w-md">
                                        {t('layouts.setup_no_templates_custom_subtitle')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Enhanced Pagination */}
                        <div className="bg-white border-t border-gray-200 px-8 py-4 shadow-lg">
                            <Paginator
                                first={currentPage * rowsPerPage}
                                rows={rowsPerPage}
                                totalRecords={totalRecords}
                                onPageChange={onPageChange}
                                className="custom-paginator"
                            />
                        </div>
                    </>
            }

            {openDialog.layoutConflict && <LayoutConflictWarning setAreas={setAreas} isLoading={isLoading} oldLayout={oldLayout} setOldLayout={setOldLayout} handleChange={handleChange} newLayout={newLayout} oldAspectRatio={oldAspectRatio} />}
        </div>
    );
};

export default ChangeCustomLayouts;